import React, { Component } from 'react';
import { matchPath, RouteComponentProps } from 'react-router';
import { withRouter } from 'react-router-dom';
import AssetManagementContext from './privateAssetManagementContext';
import AssetAccountContext from './assetAccountContext';
import {
  INVALID_ACCOUNT_IDENTIFIER,
  ROUTE,
  ROUTE_PARAMS,
  HISTORY_PUSH
} from '../utils/constants';
import NavigationContext from './navigationContext';
import history from '../utils/history';
import AppContainerSpinner from '../components/AppContainerSpinner';

type Props = RouteComponentProps &
  AppProps &
  AssetManagementData & {
    children: React.ReactNode;
  };
type State = {
  overallData?: PrivateAssetManagementContextType;
  assetAccountData?: AssetAccountContextType;
  navigationData?: NavigationContextType;
};

class PrivateAssetManagementProvider extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      overallData: undefined,
      assetAccountData: undefined,
      navigationData: undefined
    } as State;
    history.listen(this.handleBrowserButtons.bind(this));
  }

  componentDidMount() {
    const {
      data,
      applicationConfig: { urls }
    } = this.props;
    if (data && urls) {
      this.processState(data, urls);
    }
  }

  handleBrowserButtons(location: any, action: string) {
    const accountIdentifier = this.getAccountIdentifier(location.pathname);
    this.handleUpdateSelectedAccount(
      accountIdentifier,
      // only toggle if adding to history. Do not show list modal if using browser buttons.
      INVALID_ACCOUNT_IDENTIFIER === accountIdentifier ||
        HISTORY_PUSH === action
    );
  }

  getAccountIdentifier(historyPath?: string) {
    console.log('history', historyPath, this.props.location.pathname);
    const match = matchPath(historyPath || this.props.location.pathname, {
      path: ROUTE.ACCOUNT.PATH
    });
    return match
      ? match.params[ROUTE_PARAMS.ACCOUNT_ID]
        ? match.params[ROUTE_PARAMS.ACCOUNT_ID]
        : INVALID_ACCOUNT_IDENTIFIER
      : INVALID_ACCOUNT_IDENTIFIER;
  }

  processState = (
    assetManagementData: AssetManagementData,
    urls: ConfigUrls
  ) => {
    let selectedAccount: AssetAccount | undefined = undefined;
    const accountIdentifier = this.getAccountIdentifier();

    if (accountIdentifier !== INVALID_ACCOUNT_IDENTIFIER) {
      selectedAccount = this.getSelectedAccount(
        assetManagementData,
        accountIdentifier
      );
    } else if (assetManagementData.accounts.length === 1) {
      selectedAccount = assetManagementData.accounts[0];
    }

    const accountNotFound = selectedAccount === undefined;
    const overallData: PrivateAssetManagementContextType = {
      urls,
      assetManagementData,
      accountNotFound
    };

    const { userFullName, accounts, consentUserAgreement, retailEnabled } =
      assetManagementData;
    const hasMultipleAccounts = accounts.length > 1;
    const assetAccountData: AssetAccountContextType = {
      urls,
      userFullName,
      hasMultipleAccounts,
      selectedAccount,
      handleChangeAccount: this.handleChangeAccount,
      consentUserAgreement,
      retailEnabled
    };

    const navigationData: NavigationContextType = {
      urls,
      accountAccessModal: {
        show: false,
        hideNavigation: false,
        handleToggle: this.handleToggleAccountAccess
      },
      accountListModal: {
        show: selectedAccount === undefined,
        hideNavigation: selectedAccount === undefined,
        handleToggle: this.handleToggleAccountList
      },
      helpModal: {
        show: false,
        hideNavigation: false,
        handleToggle: this.handleToggleHelp
      },
      legalAgreementModal: {
        show: !consentUserAgreement,
        hideNavigation: true,
        handleToggle: this.handleToggleLegalAgreement
      },
      navigationBar: {
        show: false,
        hideNavigation: false,
        handleToggle: this.handleToggleNavigationBar
      }
    };

    this.setState({
      ...this.state,
      overallData: overallData,
      assetAccountData: assetAccountData,
      navigationData: navigationData
    });
  };

  getUpdatedNavigationDataForAccountList = () => {
    const { navigationData, assetAccountData } = this.state;

    if (navigationData === undefined || assetAccountData === undefined) {
      return undefined;
    }

    const { selectedAccount } = assetAccountData;
    const { accountListModal } = navigationData;
    const updatedAccountListModal = {
      ...accountListModal,
      show: !accountListModal.show,
      hideNavigation:
        selectedAccount === undefined ||
        this.getAccountIdentifier() === INVALID_ACCOUNT_IDENTIFIER
    };
    const updatedNavigationData: NavigationContextType = {
      ...navigationData,
      accountListModal: updatedAccountListModal
    };

    return updatedNavigationData;
  };

  handleToggleAccountAccess = () => {
    const { navigationData } = this.state;

    if (navigationData === undefined) {
      return;
    }

    const { accountAccessModal } = navigationData;
    const updatedAccountAccessModal = {
      ...accountAccessModal,
      show: !accountAccessModal.show,
      hideNavigation: accountAccessModal.hideNavigation
    };
    const updatedNavigationData: NavigationContextType = {
      ...navigationData,
      accountAccessModal: updatedAccountAccessModal
    };

    this.setState({
      ...this.state,
      navigationData: updatedNavigationData
    });
  };

  handleToggleAccountList = () => {
    this.setState({
      ...this.state,
      navigationData: this.getUpdatedNavigationDataForAccountList()
    });
  };

  handleChangeAccount = () => {
    this.handleToggleAccountList();
  };

  handleToggleHelp = () => {
    const { navigationData } = this.state;

    if (navigationData === undefined) {
      return;
    }

    const { helpModal } = navigationData;
    const updatedHelpModal = { ...helpModal, show: !helpModal.show };
    const updatedNavigationData: NavigationContextType = {
      ...navigationData,
      helpModal: updatedHelpModal
    };
    this.setState({ ...this.state, navigationData: updatedNavigationData });
  };

  handleToggleLegalAgreement = () => {
    const { navigationData } = this.state;

    if (navigationData === undefined) {
      return;
    }

    const { legalAgreementModal } = navigationData;
    const updatedLegalAgreementModal = {
      ...legalAgreementModal,
      show: !legalAgreementModal.show,
      hideNavigation: legalAgreementModal.hideNavigation
    };
    const updatedNavigationData: NavigationContextType = {
      ...navigationData,
      legalAgreementModal: updatedLegalAgreementModal
    };

    this.setState({
      ...this.state,
      navigationData: updatedNavigationData
    });
  };

  handleToggleNavigationBar = () => {
    const { navigationData } = this.state;

    if (navigationData === undefined) {
      return;
    }

    const { navigationBar } = navigationData;
    const updatedNavBar = { ...navigationBar, show: !navigationBar.show };
    const updatedNavigationData: NavigationContextType = {
      ...navigationData,
      navigationBar: updatedNavBar
    };
    this.setState({ ...this.state, navigationData: updatedNavigationData });
  };

  getSelectedAccount(
    assetManagementData: AssetManagementData | undefined,
    accountIdentifier: string
  ) {
    return assetManagementData && assetManagementData.accounts
      ? assetManagementData.accounts.find(
          (account) => account.accountIdentifier === accountIdentifier
        )
      : undefined;
  }

  handleUpdateSelectedAccount = (
    accountIdentifier: string,
    toggleAccountListModel: boolean = true
  ) => {
    const { overallData, assetAccountData, navigationData } = this.state;
    const assetManagementData = overallData
      ? overallData.assetManagementData
      : undefined;
    const selectedAccount: AssetAccount | undefined = this.getSelectedAccount(
      assetManagementData,
      accountIdentifier
    );

    if (overallData === undefined || assetAccountData === undefined) {
      return;
    }

    const newAssetAccountData: AssetAccountContextType = {
      ...assetAccountData,
      selectedAccount
    };
    let newState: State = { assetAccountData: newAssetAccountData };

    // reset flag set by initial hit with invalid identifier in url
    if (overallData && overallData.accountNotFound) {
      const accountNotFound = false;
      const newOverallData: PrivateAssetManagementContextType = {
        ...overallData,
        accountNotFound
      };
      newState.overallData = newOverallData;
    }

    newState.navigationData = toggleAccountListModel
      ? this.getUpdatedNavigationDataForAccountList()
      : navigationData;

    this.setState(newState);
  };

  render() {
    const { overallData, assetAccountData, navigationData } = this.state;
    if (
      overallData === undefined ||
      assetAccountData === undefined ||
      navigationData === undefined
    ) {
      return <AppContainerSpinner />;
    }

    return (
      <AssetManagementContext.Provider value={overallData}>
        <AssetAccountContext.Provider value={assetAccountData}>
          <NavigationContext.Provider value={navigationData}>
            {this.props.children}
          </NavigationContext.Provider>
        </AssetAccountContext.Provider>
      </AssetManagementContext.Provider>
    );
  }
}

export default withRouter(PrivateAssetManagementProvider);
